import { Box, Typography } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import { useMemo } from 'react';
import { ResponsiveContainer, Tooltip, Treemap } from 'recharts';
import { truncateTo2dp } from 'utils/helpers/general';
import { formatNumberOrString } from 'utils/helpers/numbers';
import { type fuelMeasureType } from 'views/FuelHistoricalAnalysis/types/fuelAnalytics.types';
import { fuelValueConversion } from 'views/FuelHistoricalAnalysis/utils';
import { getFuelUsageDataUnit } from 'views/FuelUsage/utils';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { cloneDeep } from 'lodash';

const FUEL_MEASURE_LABEL_KEYS: Record<fuelMeasureType, string> = {
  consumption: 'fuel_usage_dropdown_consumption',
  co2_emission: 'fuel_usage_dropdown_co2_emission',
  cost: 'cost',
  num_of_idle_events: 'fuel_usage_dropdown_num_of_idle_events',
  total_duration_of_idle_events:
    'fuel_usage_dropdown_total_duration_of_idle_events',
  loaded_fuel_consumption: 'fuel_usage_dropdown_loaded_fuel_consumption',
  unloaded_fuel_consumption: 'fuel_usage_dropdown_unloaded_fuel_consumption',
  avg_fuel_rate: 'fuel_usage_dropdown_avg_fuel_rate',
  fuel_consumed_per_ton: 'fuel_usage_dropdown_fuel_consumed_per_ton',
  overspeeding: 'fuel_usage_event_overspeeding',
  underspeeding: 'fuel_usage_event_underspeeding',
  gears: 'fuel_usage_dropdown_gears',
  short_idling: 'fuel_usage_event_short_idling',
  long_idling: 'fuel_usage_event_long_idling',
  queuing: 'fuel_usage_dropdown_queuing',
};

const TreeMapDiv = ({
  handleClicked,
  data,
  fuelMeasure,
  baseCost,
  selectedAssetType,
  isLoading,
  unitSystem,
  fuelUnit,
  sortBy,
  externalTotal,
}: {
  handleClicked: (device: string) => void;
  data: any;
  fuelMeasure: fuelMeasureType;
  baseCost: {
    diesel: number;
    gas: number;
  };
  selectedAssetType: string;
  isLoading: boolean;
  unitSystem: string;
  fuelUnit: 'ltr' | 'gal';
  sortBy?: 'low to high' | 'high to low';
  externalTotal?: number | null;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const treemapFill = CLIENT_THEME === 'yokohama' ? palette.primary : '#4A7BFA';
  const treemapStroke = palette.black;

  let totalSize = 0;
  let totalFuel = 0;
  let totalHours = 0;
  const transformedData = data
    ?.map((item: any) => {
      let size = 0;
      switch (fuelMeasure) {
        case 'consumption':
          size = +fuelValueConversion(
            +item.total_fuel_consumption,
            unitSystem,
            fuelUnit
          ).toFixed(2);
          break;
        case 'co2_emission':
          size = +(+item.CO2).toFixed(2);
          break;
        case 'total_duration_of_idle_events':
          size = +(+item.total_duration_of_idle_events).toFixed(2);
          break;
        case 'cost':
          size = +(
            fuelValueConversion(
              +item.total_fuel_consumption,
              unitSystem,
              fuelUnit,
              'cost'
            ) * baseCost[item.fuelSource.toLowerCase() as 'gas' | 'diesel']
          ).toFixed(2);
          break;
        case 'avg_fuel_rate': {
          const fuel = +fuelValueConversion(
            +item.total_fuel_consumption,
            unitSystem,
            fuelUnit
          );

          const avgRate = +fuelValueConversion(
            +item.avg_fuel_rate,
            unitSystem,
            fuelUnit
          );

          const hours = avgRate > 0 ? fuel / avgRate : 0;

          size = avgRate;

          if (!isNaN(fuel) && !isNaN(hours)) {
            totalFuel += fuel;
            totalHours += hours;
          }
          break;
        }
        case 'loaded_fuel_consumption':
        case 'unloaded_fuel_consumption':
          size = +fuelValueConversion(
            +item[fuelMeasure],
            unitSystem,
            fuelUnit
          ).toFixed(2);
          break;
        default:
          size = Number(item?.[fuelMeasure]) ?? 0;
      }

      // Add to totalSize for non-average measures
      if (fuelMeasure !== 'avg_fuel_rate' && !isNaN(size)) {
        totalSize += size;
      }
      if (
        selectedAssetType === 'haul_truck' &&
        item.asset_type === 'haul_truck'
      ) {
        return {
          name: item.bumper_number,
          size,
        };
      } else if (selectedAssetType === item.asset_type) {
        return {
          name: item.bumper_number,
          size,
        };
      }
      return {
        name: item.bumper_number,
        size,
      };
      // return null;
    })
    .filter(Boolean);

  if (externalTotal !== undefined && externalTotal !== null) {
    if (
      fuelMeasure === 'consumption' ||
      fuelMeasure === 'avg_fuel_rate' ||
      fuelMeasure === 'cost' ||
      fuelMeasure === 'fuel_consumed_per_ton'
    ) {
      totalSize = fuelValueConversion(
        externalTotal,
        unitSystem,
        fuelUnit,
        fuelMeasure === 'cost' ? 'cost' : undefined
      );
    } else {
      totalSize = externalTotal;
    }
  } else if (fuelMeasure === 'avg_fuel_rate') {
    totalSize = totalHours > 0 ? totalFuel / totalHours : 0;
  }

  const sortedData = useMemo(() => {
    const clone = cloneDeep(transformedData);
    clone?.sort((a: any, b: any) => {
      if (sortBy === 'low to high') {
        return a.size - b.size;
      } else {
        return b.size - a.size;
      }
    });
    return clone;
  }, [transformedData]);

  const unit = useMemo(
    () => getFuelUsageDataUnit(fuelMeasure, unitSystem),
    [fuelMeasure, unitSystem]
  );

  return (
    <Box
      sx={{
        // padding: '30px 20px',
        border: `1px solid ${palette.cardBorderColor}`,
        borderRadius: '4px',
        width: '100%',
        // height: '100%',
      }}
    >
      {!sortedData?.length && !isLoading ? (
        <Box
          sx={{
            padding: '50px',
            border: `1px solid ${palette.cardBorderColor}`,
            borderRadius: '4px',
            color: palette.textPrimary,
          }}
        >
          <Typography>{t('no_data_available_yet')}</Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              my: '5px',
              color: palette.textPrimary,
            }}
          >
            <Typography
              style={{
                fontSize: '13px',
                fontWeight: 'bold',
              }}
            >
              {/* change this to selected tiem frame */}
              {/* 17 Sept - 23 Oct */}
            </Typography>
            {true && (
              <Typography style={{ fontSize: '13px', fontWeight: 'bold' }}>
                <span
                  style={{ fontWeight: 'normal', textTransform: 'capitalize' }}
                >
                  {t(FUEL_MEASURE_LABEL_KEYS[fuelMeasure] ?? fuelMeasure)} :
                </span>{' '}
                {formatNumberToShortScale(truncateTo2dp(totalSize || 0), 2)}{' '}
                {unit}
              </Typography>
            )}
          </Box>
          <ResponsiveContainer width="100%" height={230}>
            <Treemap
              data={sortedData}
              dataKey="size"
              aspectRatio={4 / 3}
              stroke={treemapStroke}
              fill={treemapFill}
              content={
                <CustomTreemapContent
                  handleClicked={handleClicked}
                  unit={unit ?? ''}
                  formatToShortScale
                  fill={treemapFill}
                  stroke={treemapStroke}
                />
              }
            >
              <Tooltip
                content={({ payload }) => {
                  if (payload && payload.length > 0) {
                    const { name, value } = payload[0].payload;
                    return (
                      <div
                        style={{
                          backgroundColor: '#fee',
                          padding: '6px',
                          border: `1px solid ${palette.cardBorderColor}`,
                          color: '#000',
                        }}
                      >
                        <p style={{ fontSize: 'smaller' }}>
                          {t('asset_name')} <b>{name}</b>
                        </p>
                        <p style={{ fontSize: 'smaller' }}>
                          {t(
                            FUEL_MEASURE_LABEL_KEYS[fuelMeasure] ?? fuelMeasure
                          )}
                          :{' '}
                          <b>
                            {formatNumberToShortScale(value?.toString(), 2)}
                          </b>{' '}
                          {unit}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </Treemap>
          </ResponsiveContainer>
        </>
      )}
    </Box>
  );
};

const CustomTreemapContent = ({
  depth = 0,
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  name = '',
  size = '',
  handleClicked,
  unit = '',
  formatToShortScale = false,
  fill = '#4A7BFA',
  stroke = '#000',
}: {
  depth?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  name?: string;
  size?: string;
  handleClicked: (device: string) => void;
  unit: string;
  formatToShortScale?: boolean;
  fill?: string;
  stroke?: string;
}) => {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        stroke={stroke}
        strokeWidth={3}
        fill={fill}
        onClick={() => {
          handleClicked(name);
        }}
        style={{ cursor: 'pointer' }}
      />
      {width > 50 && height > 20 && (
        <>
          <text
            x={x + width / 2}
            y={y + height / 4}
            textAnchor="middle"
            fill="#fff"
            stroke="none"
            style={{ fontSize: 14, fontWeight: 'bold' }}
          >
            {name}
          </text>

          <text
            x={x + width / 2}
            y={y + height / 2}
            textAnchor="middle"
            fill="#fff"
            stroke="none"
            style={{ fontSize: 10, fontWeight: 'bold' }}
          >
            {formatToShortScale
              ? formatNumberToShortScale(size?.toString(), 2)
              : formatNumberOrString(size?.toString())}{' '}
            {unit}
          </text>
        </>
      )}
    </g>
  );
};

export default TreeMapDiv;
